import { Icon, IconButton, Menu } from '@zillow/constellation-10';
import { IconMoreVerticalFilled } from '@zillow/constellation-icons';

import { toAppRoute } from '../../../constants/urls';

import type { NextRouter } from 'next/router';
import type {
  AccountViewListingOptionType,
  AccountViewPropertyOptionDto,
  AccountViewPropertyUnitOptionDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type OverflowMenuItem = {
  label: string;
  onSelect?: () => void;
};

type ApiOverflowOption = AccountViewPropertyOptionDto | AccountViewPropertyUnitOptionDto;

/**
 * Builds overflow menu items from API row options. By default each item navigates to its `ctaUrl`;
 * pass `actionOverrides` to override specific option types with a custom action (e.g. opening a modal).
 */
export const buildOverflowMenuItems = (
  options: Array<ApiOverflowOption>,
  {
    router,
    actionOverrides = {},
  }: {
    router: NextRouter;
    actionOverrides?: Partial<Record<AccountViewListingOptionType, () => void>>;
  },
): Array<OverflowMenuItem> =>
  options.map(({ type, ctaText, ctaUrl }) => {
    const override = actionOverrides[type];

    return {
      label: ctaText,
      onSelect: override ?? (ctaUrl ? () => router.push(toAppRoute(ctaUrl)) : undefined),
    };
  });

type Props = {
  items: Array<OverflowMenuItem>;
  triggerLabel: string;
  disabled?: boolean;
};

const OverflowMenu = ({ items, triggerLabel, disabled = false }: Props) => {
  const stopRowNavigation = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Menu.Root>
      <Menu.Trigger>
        <IconButton
          aria-label={triggerLabel}
          tone="brand"
          emphasis="bare"
          title={triggerLabel}
          size="md"
          disabled={disabled}
          onClick={stopRowNavigation}
          data-testid="OverflowMenu-Trigger"
        >
          <Icon render={<IconMoreVerticalFilled />} />
        </IconButton>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content>
          {items.map((item) => (
            <Menu.Item
              key={item.label}
              onClick={(event) => {
                stopRowNavigation(event);
                item.onSelect?.();
              }}
            >
              <Menu.ItemLabel>{item.label}</Menu.ItemLabel>
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
};

export { OverflowMenu };
